/**
 * In-app events (docs/APP-EVENTS.md): what signed-in people of a BUSINESS do in the phone app and the web app, so we can see which
 * screens are used, which buttons are tapped, where people stop, and whether a "Find it on Amazon" tap ended in "I got it".
 * Owner, 2026-10-07: "track if they click the buttons and which buttons they click, like going to Amazon, so that we know if they
 * actually purchased it ... get data about what people are actually doing in the application."
 *
 * This file is the pure part both apps and the server share. The rules it enforces:
 *  E1  Never a health screen (FRAMEWORK T4): a route with any HEALTH_PATH_PARTS part sends nothing at all, not a view, not a tap.
 *  E2  Never a value: a control is named by a string a developer wrote (a literal testID, a testID's literal prefix, or a literal
 *      button label; the apps' generated control lists), never by what is on screen at the time (names, typed text, questions).
 *  E3  A small typed target only: a catalog item key (SUPPLY_SEEDS or a product purpose), an outbound host, a placement, a flow
 *      and stage. A custom supply (a slug of typed words) is never an item.
 *  E4  The server takes the business and the person from the sign-in, never from the body; the body is checked key by key.
 *  E5  "Did you get it?" asks once per click, at least a day after it, and never more than once a week per item (gotit* below).
 */
import { SUPPLY_SEEDS } from "./actions.ts";
import { isPurposeId, PRODUCT_PURPOSES } from "./products.ts";
import { HEALTH_PATH_PARTS } from "./siteanalytics.ts";
import { HAVE_ITEMS } from "./home/have.ts";

/* ─────────────── vocabulary ─────────────── */

/** "phone-web" is the phone app's own web export (Expo web); "web" is the web app (apps/web). */
export const APP_PLATFORMS = ["ios", "android", "phone-web", "web"] as const;
export type AppPlatform = (typeof APP_PLATFORMS)[number];

/** view: a screen opened (with the screen before it). leave: a screen closed, with its active time. tap: a button. out: a link that leaves the app. got_it / not_yet: the answer to "Did you get the …?". flow: a step of a key flow. */
export const APP_EVENT_KINDS = ["view", "leave", "tap", "out", "got_it", "not_yet", "flow"] as const;
export type AppEventKind = (typeof APP_EVENT_KINDS)[number];

/**
 * Where a buy link sat. Each can carry its own Amazon tracking ID (amazonTagFor), so Amazon's own reports (aggregate per tracking
 * ID; Amazon gives no per-person purchase data) say which placement sells.
 */
export const BUY_PLACEMENTS = ["step", "list", "ask", "home-guide", "term", "shop-kit", "order", "routine", "check-fix", "supply", "how-to", "plan-supply", "other"] as const;
export type BuyPlacement = (typeof BUY_PLACEMENTS)[number];
export const BUY_PLACEMENT_LABEL: Record<BuyPlacement, string> = {
  step: "A step's “Need it?” link", list: "A list's buy button", ask: "Ask's Buy list", "home-guide": "A home guide's “You need”",
  term: "A term card", "shop-kit": "Shop the starter kit", order: "Order", routine: "A routine's supplies", "check-fix": "A check's easy fix",
  supply: "A supply's page", "how-to": "A how-to", "plan-supply": "A cited plan's “Get it” (never tagged)", other: "Somewhere else",
};

/** The key flows whose drop-off the Data dashboard shows: started → finished. (Business setup is the Funnel tab's, counted on the server.) */
export const APP_FLOWS = ["onboarding", "ask", "guide"] as const;
export type AppFlow = (typeof APP_FLOWS)[number];
export const APP_FLOW_LABEL: Record<AppFlow, string> = {
  onboarding: "New-hire onboarding (phone)", ask: "Ask: asked → got an answer", guide: "Step-by-step guide: started → last step",
};
export const FLOW_STAGES = ["start", "finish"] as const;
export type FlowStage = (typeof FLOW_STAGES)[number];

/* ─────────────── sessions and time on screen (owner, 2026-10-07: "logging page times ... path tracking") ─────────────── */

/** One screen view counts at most 30 minutes of active time, so a screen left open doesn't distort the averages. */
export const MAX_SCREEN_MS = 30 * 60_000;
/** A new session after 30 minutes in the background or with nothing done. */
export const SESSION_IDLE_MS = 30 * 60_000;
/** A session id: random, made on the device per app open; never a device id. Kept only with the raw events (13 months). */
export const SESSION_PATTERN = /^[a-z0-9]{10,16}$/;
export const newSessionId = (rand: () => number = Math.random): string => Array.from({ length: 12 }, () => "abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(rand() * 36)]).join("");
/** The previous screen when it was one we never measure (a health screen): the path shows a gap, never the screen. */
export const PATH_GAP = "gap";

/* ─────────────── screens (E1) ─────────────── */

/** A screen is a route PATTERN ("/supplies/item/:key", "/library/[id]"), never a filled-in address. */
export const SCREEN_PATTERN = /^\/[A-Za-z0-9_\-/:[\]]{0,99}$/;

/** True when the screen may be measured: a route pattern with no health part anywhere in it (E1). */
export function appScreenAllowed(screen: unknown): screen is string {
  if (typeof screen !== "string" || !SCREEN_PATTERN.test(screen)) return false;
  return !screen.split("/").some((part) => HEALTH_PATH_PARTS.has(part.toLowerCase()));
}

/** The phone's screen from expo-router's segments (["(tabs)", "today"] → "/today"; ["library", "[id]"] → "/library/[id]"). */
export function phoneScreen(segments: readonly string[]): string {
  return `/${segments.filter((s) => !/^\(.*\)$/.test(s)).join("/")}`;
}

/**
 * The web app's screen: the route pattern (App.tsx `<Route path>`) the address fits, or null when none does (the catch-all "*" is
 * never a screen). "supplies/item/:key" fits "/supplies/item/gloves" → "/supplies/item/:key". Literal patterns win over params.
 */
export function webScreen(pathname: string, patterns: readonly string[]): string | null {
  const parts = pathname.split("/").filter(Boolean);
  let best: { p: string; score: number } | null = null;
  for (const p of patterns) {
    const pp = p.split("/").filter(Boolean);
    if (pp.length !== parts.length || pp.includes("*")) continue;
    let score = 0, ok = true;
    for (let i = 0; i < pp.length; i++) {
      if (pp[i]!.startsWith(":")) continue;
      if (pp[i] !== parts[i]) { ok = false; break; }
      score++;
    }
    if (ok && (!best || score > best.score)) best = { p, score };
  }
  return best ? `/${best.p.split("/").filter(Boolean).join("/")}` : null;
}

/* ─────────────── controls (E2) ─────────────── */

export const CONTROL_PATTERN = /^(?:id:[A-Za-z0-9][A-Za-z0-9_.-]{0,62}\*?|label:[a-z0-9][a-z0-9-]{0,62}|button|link)$/;

/** The control lists an app generates from its own source (scripts/gen-app-controls.ts): literal testIDs, testID prefixes, labels. */
export interface ControlList { ids: readonly string[]; prefixes: readonly string[]; labels: readonly string[] }
/** Who may open a web route (scripts/gen-app-controls.ts scanWebRouteGates: App.tsx RequireRole / RequireSwitch), for core appsearch.ts. */
export interface RouteGate { min?: "staff" | "manager" | "admin" | "owner"; sw?: "billing" | "people" | "settings" }
/** A route's gate in a business's routes and in a home's; a side that is absent has no such route. */
export interface RouteGates { business?: RouteGate; home?: RouteGate }
export interface ControlIndex { ids: Set<string>; prefixes: string[]; labels: Set<string>; slugs: Set<string> }
export const controlIndex = (l: ControlList): ControlIndex => ({ ids: new Set(l.ids), prefixes: [...l.prefixes].sort((a, b) => b.length - a.length), labels: new Set(l.labels), slugs: new Set(l.labels.map((x) => labelSlug(x))) });

/** The server's second check (E2): a control name is one an app's list could have made, or the batch is refused. */
export function knownControl(idx: ControlIndex, c: string): boolean {
  if (c === "button" || c === "link") return true;
  if (c.startsWith("label:")) return idx.slugs.has(c.slice(6));
  if (c.startsWith("id:")) { const v = c.slice(3); return v.endsWith("*") ? idx.prefixes.includes(v.slice(0, -1)) : idx.ids.has(v); }
  return false;
}

export function labelSlug(label: string): string { return label.toLowerCase().replace(/[’']/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 63); }

/**
 * A control's stable name (E2): its testID when a developer wrote it whole ("id:buy-gloves"); a testID built from a literal prefix
 * and a value is named by the prefix only ("chore-done-kitchen" → "id:chore-done-*"); else a label a developer wrote
 * ("label:find-it-on-amazon"). Anything else (a label holding a name or an item someone typed) gets only its kind, or nothing.
 */
export function controlName(idx: ControlIndex, o: { testID?: string | null; label?: string | null; kind?: "button" | "link" }): string | null {
  const id = o.testID?.trim();
  if (id) {
    if (idx.ids.has(id) && CONTROL_PATTERN.test(`id:${id}`)) return `id:${id}`;
    const pre = idx.prefixes.find((p) => id.startsWith(p) && id.length > p.length);
    if (pre && CONTROL_PATTERN.test(`id:${pre}*`)) return `id:${pre}*`;
  }
  const label = o.label?.trim();
  if (label && idx.labels.has(label)) { const k = `label:${labelSlug(label)}`; if (CONTROL_PATTERN.test(k)) return k; }
  return o.kind ?? null;
}

/* ─────────────── targets (E3) ─────────────── */

const SUPPLY_KEYS = new Set(SUPPLY_SEEDS.map((s) => s.key));
/** A home's checklist items ("color-cloths"): catalog keys too, so a home's buy taps and "I got it" count (core home/have.ts). */
const HOME_ITEM_KEYS = new Set(HAVE_ITEMS.map((i) => i.id));
export const isHomeItem = (v: unknown): v is string => typeof v === "string" && HOME_ITEM_KEYS.has(v);
/** The supply key a catalog item restocks: a product purpose's own (probe_thermometer → thermometer), or the item itself. */
export const catalogSupplyKey = (item: string): string => PRODUCT_PURPOSES.find((p) => p.id === item)?.supplyKey ?? item;

/** A catalog item's plain name ("single-use gloves"), for "Did you get the …?" and the Data dashboard; undefined when it isn't one. */
export const catalogItemName = (item: string): string | undefined => PRODUCT_PURPOSES.find((p) => p.id === item)?.name ?? SUPPLY_SEEDS.find((s) => s.key === item)?.name ?? HAVE_ITEMS.find((i) => i.id === item)?.name;

/** A catalog item: a seeded supply key or a product purpose. Never a custom supply (its key is a slug of typed words). */
export const isCatalogItem = (v: unknown): v is string => typeof v === "string" && /^[a-z0-9][a-z0-9_-]{0,47}$/.test(v) && (SUPPLY_KEYS.has(v) || isPurposeId(v) || HOME_ITEM_KEYS.has(v));

const HOST = /^(?=.{3,80}$)[a-z0-9-]+(?:\.[a-z0-9-]+)+$/;
/** The host of an http(s) link, without "www." ("amazon.com"); null for anything else (tel:, mailto:, a bad URL). */
export function outboundHost(href: string): string | null {
  try {
    const u = new URL(href);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    const h = u.hostname.toLowerCase().replace(/^www\./, "");
    return HOST.test(h) ? h : null;
  } catch { return null; }
}
export const isAmazonHost = (h: string | null) => !!h && /(^|\.)amazon\.[a-z.]{2,6}$/.test(h);

/* ─────────────── Amazon tracking IDs per placement ─────────────── */

const TAG = /^[A-Za-z0-9][A-Za-z0-9_-]{1,39}$/;
/**
 * "step=cleancleav-step-20,ask=cleancleav-ask-20" (EXPO_PUBLIC_AMAZON_TAGS / VITE_AMAZON_TAGS) → a map. The owner creates the IDs
 * in Associates Central; none is invented here. Unknown placements and odd values are ignored.
 */
export function parsePlacementTags(raw: string | undefined | null): Partial<Record<BuyPlacement, string>> {
  const out: Partial<Record<BuyPlacement, string>> = {};
  for (const pair of (raw ?? "").split(",")) {
    const [k, v] = pair.split("=").map((x) => x?.trim() ?? "");
    if (k && v && (BUY_PLACEMENTS as readonly string[]).includes(k) && TAG.test(v)) out[k as BuyPlacement] = v;
  }
  return out;
}

/** Placements whose links are never tagged: a cited plan's "Get it" says "We don't sell these or get paid for them" (citedplan.ts SUPPLY_NOTE). */
export const UNTAGGED_PLACEMENTS: readonly BuyPlacement[] = ["plan-supply"];

/**
 * The tag a placement's Amazon link carries: its own ID when the owner set one, else the default. Only when a default is set at
 * all: the Associates disclosure line shows only then (FTC 16 CFR 255.5), so a placement ID alone never tags a link.
 */
export function amazonTagFor(placement: BuyPlacement | undefined, defaultTag: string | undefined, tags: Partial<Record<BuyPlacement, string>>): string | undefined {
  if (!defaultTag || UNTAGGED_PLACEMENTS.includes(placement as BuyPlacement)) return undefined;
  return (placement && tags[placement]) || defaultTag;
}

/** An Amazon link with its tag set for the placement (other links unchanged). */
export function retagAmazon(href: string, placement: BuyPlacement | undefined, defaultTag: string | undefined, tags: Partial<Record<BuyPlacement, string>>): string {
  if (!isAmazonHost(outboundHost(href))) return href;
  const tag = amazonTagFor(placement, defaultTag, tags);
  if (!tag) return href;
  try { const u = new URL(href); u.searchParams.set("tag", tag); return u.toString(); } catch { return href; }
}

/* ─────────────── the wire format (E4) ─────────────── */

/** One event as an app sends it. Short keys; anything else refuses the batch. */
export interface WireEvent {
  /** When, ms since 1970. */ t: number;
  k: AppEventKind;
  /** The screen (route pattern). */ s: string;
  /** The control (controlName). */ c?: string;
  /** A catalog item key. */ i?: string;
  /** An outbound host. */ h?: string;
  pl?: BuyPlacement;
  f?: AppFlow;
  st?: FlowStage;
  /** The session (newSessionId). */ sid?: string;
  /** On a view: the screen before it in this session, or "gap" (a screen never measured); none = the session's first screen. */ pv?: string;
  /** On a leave: active milliseconds on the screen (paused in the background), at most MAX_SCREEN_MS. */ ms?: number;
}
export interface WireBatch { v: 1; p: AppPlatform; av: string; e: WireEvent[] }
export const EVENT_KEYS = ["t", "k", "s", "c", "i", "h", "pl", "f", "st", "sid", "pv", "ms"] as const;
export const BATCH_KEYS = ["v", "p", "av", "e"] as const;
/** The apps send at most BATCH_SIZE (100, appevents-tracker.ts) per batch; the server takes no more (security run-3 M1). */
export const MAX_EVENTS_PER_BATCH = 100;
export const MAX_EVENT_BATCH_BYTES = 48_000;
/** A phone may hold events offline this long; older ones are dropped on arrival. */
export const EVENT_MAX_AGE_MS = 14 * 86_400_000;
export const APP_VERSION_PATTERN = /^[0-9A-Za-z.+-]{1,20}$/;

/** A checked event, ready to keep (the server adds the business, the person and the day). */
export interface AppEvent { at: string; kind: AppEventKind; screen: string; control: string; item: string; host: string; placement: string; flow: string; stage: string; sid: string; prev: string; ms: number | null }

const isOf = <T extends string>(list: readonly T[], v: unknown): v is T => typeof v === "string" && (list as readonly string[]).includes(v);

/** One event, strictly. Null = drop it. `"refuse"` = the batch carries something it never should (the whole batch is refused). */
export function parseWireEvent(raw: unknown, nowMs: number): AppEvent | null | "refuse" {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return "refuse";
  const o = raw as Record<string, unknown>;
  if (Object.keys(o).some((k) => !(EVENT_KEYS as readonly string[]).includes(k))) return "refuse";
  if (!isOf(APP_EVENT_KINDS, o.k)) return "refuse";
  if (typeof o.t !== "number" || !Number.isInteger(o.t)) return "refuse";
  if (o.c !== undefined && (typeof o.c !== "string" || !CONTROL_PATTERN.test(o.c))) return "refuse";
  if (o.i !== undefined && !isCatalogItem(o.i)) return "refuse";
  if (o.h !== undefined && (typeof o.h !== "string" || !HOST.test(o.h))) return "refuse";
  if (o.pl !== undefined && !isOf(BUY_PLACEMENTS, o.pl)) return "refuse";
  if (o.f !== undefined && !isOf(APP_FLOWS, o.f)) return "refuse";
  if (o.st !== undefined && !isOf(FLOW_STAGES, o.st)) return "refuse";
  if (typeof o.s !== "string" || !SCREEN_PATTERN.test(o.s)) return "refuse";
  if (o.sid !== undefined && (typeof o.sid !== "string" || !SESSION_PATTERN.test(o.sid))) return "refuse";
  if (o.pv !== undefined && (o.k !== "view" || typeof o.pv !== "string" || (o.pv !== PATH_GAP && !SCREEN_PATTERN.test(o.pv)))) return "refuse";
  if (o.ms !== undefined && (o.k !== "leave" || typeof o.ms !== "number" || !Number.isInteger(o.ms) || o.ms < 0)) return "refuse";
  if (o.k === "leave" && o.ms === undefined) return "refuse";
  if (!appScreenAllowed(o.s)) return null; // E1: dropped, whatever sent it
  if (o.t > nowMs + 5 * 60_000 || o.t < nowMs - EVENT_MAX_AGE_MS) return null;
  if (o.k === "flow" && (!o.f || !o.st)) return "refuse";
  if ((o.k === "got_it" || o.k === "not_yet") && !o.i) return "refuse";
  return {
    at: new Date(o.t).toISOString(), kind: o.k, screen: o.s, control: (o.c as string) ?? "", item: (o.i as string) ?? "", host: (o.h as string) ?? "",
    placement: (o.pl as string) ?? "", flow: (o.f as string) ?? "", stage: (o.st as string) ?? "", sid: (o.sid as string) ?? "",
    // A previous screen we never measure is a gap in the path, never its name (E1).
    prev: o.pv === undefined ? "" : o.pv === PATH_GAP || !appScreenAllowed(o.pv) ? PATH_GAP : (o.pv as string),
    ms: o.k === "leave" ? Math.min(o.ms as number, MAX_SCREEN_MS) : null,
  };
}

export interface ParsedBatch { platform: AppPlatform; version: string; events: AppEvent[]; dropped: number }
/** A batch, strictly: null refuses it all (an unexpected key, a value, a wrong type anywhere). */
export function parseEventBatch(raw: unknown, nowMs: number): ParsedBatch | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (Object.keys(o).some((k) => !(BATCH_KEYS as readonly string[]).includes(k))) return null;
  if (o.v !== 1 || !isOf(APP_PLATFORMS, o.p) || typeof o.av !== "string" || !APP_VERSION_PATTERN.test(o.av)) return null;
  if (!Array.isArray(o.e) || o.e.length > MAX_EVENTS_PER_BATCH) return null;
  const events: AppEvent[] = [];
  let dropped = 0;
  for (const e of o.e) {
    const r = parseWireEvent(e, nowMs);
    if (r === "refuse") return null;
    if (r === null) dropped++; else events.push(r);
  }
  return { platform: o.p, version: o.av, events, dropped };
}

/* ─────────────── consent ─────────────── */

/**
 * "Share usage data" (Settings / More, on both apps): OFF until the person says yes (owner, 2026-10-09: the 10-08 rule "no analytics
 * before consent" wins over 10-07's default on). Homes and businesses alike. The person's own choice wins; Do Not Track or Global
 * Privacy Control in a browser turns it off whatever the choice. The server also keeps nothing without the usage_data yes.
 */
export function usageSharingOn(o: { signedIn: boolean; choice: boolean | null; dnt?: boolean }): boolean {
  if (!o.signedIn || o.dnt) return false;
  return o.choice ?? false;
}

/* ─────────────── "Did you get it?" (E5) ─────────────── */

export const GOT_IT_AFTER_MS = 86_400_000;
export const GOT_IT_GAP_MS = 7 * 86_400_000;
/** A click nobody answered for is forgotten after this long. */
export const GOT_IT_FORGET_MS = 45 * 86_400_000;

/** One item someone went to buy, kept on their own device only (the name is for the question; it is never sent). */
export interface PendingBuy { item: string; name: string; placement: BuyPlacement; clickedAt: number; askedForClick: boolean; lastAskedAt?: number }
export type GotItState = Record<string, PendingBuy>;

/** A buy link was opened: remember it (a new click on the same item resets the click, never the weekly limit). */
export function noteBuyClick(st: GotItState, b: { item: string; name: string; placement: BuyPlacement }, nowMs: number): GotItState {
  if (!isCatalogItem(b.item)) return st;
  const prev = st[b.item];
  return { ...st, [b.item]: { item: b.item, name: b.name.slice(0, 60), placement: b.placement, clickedAt: nowMs, askedForClick: false, ...(prev?.lastAskedAt ? { lastAskedAt: prev.lastAskedAt } : {}) } };
}

/**
 * The one item to ask about now, or null: a click at least a day old, not asked about since that click, and not asked about in
 * the last 7 days. `item` limits it to one item (its own screen); without it, the oldest click (Today).
 */
export function dueGotIt(st: GotItState, nowMs: number, item?: string): PendingBuy | null {
  const due = Object.values(st)
    .filter((p) => (!item || p.item === item || catalogSupplyKey(p.item) === item) && !p.askedForClick && nowMs - p.clickedAt >= GOT_IT_AFTER_MS && nowMs - p.clickedAt < GOT_IT_FORGET_MS && (p.lastAskedAt === undefined || nowMs - p.lastAskedAt >= GOT_IT_GAP_MS))
    .sort((a, b) => a.clickedAt - b.clickedAt);
  return due[0] ?? null;
}

/** The question was shown: it counts as asked (once per click, once a week), whether or not it is answered. */
export function markGotItAsked(st: GotItState, item: string, nowMs: number): GotItState {
  const p = st[item];
  return p ? { ...st, [item]: { ...p, askedForClick: true, lastAskedAt: nowMs } } : st;
}

/** Yes: done with it. Not yet: kept, so a later click can ask again (after the week). */
export function answerGotIt(st: GotItState, item: string, answer: "yes" | "not_yet", nowMs: number): GotItState {
  if (answer === "yes") { const { [item]: _gone, ...rest } = st; return rest; }
  return markGotItAsked(st, item, nowMs);
}

/** Drops clicks older than GOT_IT_FORGET_MS. */
export function pruneGotIt(st: GotItState, nowMs: number): GotItState {
  return Object.fromEntries(Object.entries(st).filter(([, p]) => nowMs - p.clickedAt < GOT_IT_FORGET_MS));
}

/** "Did you get the gloves?" */
export const gotItQuestion = (name: string) => `Did you get the ${name.trim().replace(/^(the|a|an)\s+/i, "").toLowerCase()}?`;

/* ─────────────── retention ─────────────── */

/** Raw events are kept 13 months, then only the daily counts (no business, no person). */
export const RAW_EVENT_MONTHS = 13;
/** The first day whose raw events are kept, for `day` (YYYY-MM-DD, Utah). */
export function rawEventCutoff(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1 - RAW_EVENT_MONTHS, 1));
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), Math.min(d, last))).toISOString().slice(0, 10);
}

/* ─────────────── the report ─────────────── */

/** Counts over a range, grouped by everything but the day. `users` = distinct people (0 for days older than the raw events). */
export interface EventCount { platform: string; kind: string; screen: string; control: string; item: string; host: string; placement: string; flow: string; stage: string; n: number; users: number }
/** The same per day, for the CSV. */
export interface DailyEventCount extends Omit<EventCount, "users"> { day: string }
export interface ActiveCount { start: string; orgs: number; users: number }
export interface BuyCount { item: string; placement: string; clicks: number; clickers: number; gotIt: number; notYet: number }

/** The dashboard's filters: phone or web, the business kind, the person's role. */
export interface AppFilter { platform?: "phone" | "web"; orgKind?: string; role?: string }
export const APP_FILTER_ROLES = ["owner", "admin", "manager", "staff"] as const;
export const APP_FILTER_KINDS = ["coffee_shop", "restaurant", "community_space", "home"] as const;
/** A dashboard filter from the query, keeping only known values. */
export function appFilterOf(q: { platform?: string; kind?: string; role?: string }): AppFilter {
  return {
    ...(q.platform === "phone" || q.platform === "web" ? { platform: q.platform } : {}),
    ...((APP_FILTER_KINDS as readonly string[]).includes(q.kind ?? "") ? { orgKind: q.kind } : {}),
    ...((APP_FILTER_ROLES as readonly string[]).includes(q.role ?? "") ? { role: q.role } : {}),
  };
}
export interface ScreenTime { screen: string; n: number; median: number; p75: number }
export interface Transition { from: string; to: string; n: number }
export interface TimelineRow { at: string; sid: string; platform: string; kind: string; screen: string; prev: string; ms: number | null; control: string; item: string; placement: string }

export interface AppReport {
  from: string; to: string;
  filter: AppFilter;
  /** Active time per screen (paused in the background, each view at most 30 minutes), slowest median first. */
  times: ScreenTime[];
  /** Screens sessions start on, and end on. */
  entries: { screen: string; n: number }[];
  exits: { screen: string; n: number }[];
  /** Screen → next screen (a session's first screen has from ""; "gap" stands for a screen never measured). */
  transitions: Transition[];
  active: { days: ActiveCount[]; weeks: ActiveCount[] };
  totals: { views: number; taps: number; outbound: number; gotIt: number };
  screens: { screen: string; views: number; people: number; platforms: string[] }[];
  buttons: { screen: string; taps: number; controls: { control: string; taps: number; people: number }[] }[];
  flows: { flow: AppFlow | "buy"; label: string; started: number; finished: number; pct: number | null; dropOff: number | null }[];
  buys: { item: string; placement: string; placementLabel: string; clicks: number; people: number; gotIt: number; notYet: number; rate: number | null }[];
}

const pct1 = (n: number, of: number): number | null => (of > 0 ? Math.round((n / of) * 1000) / 10 : null);

export function appReport(r: { from: string; to: string; counts: readonly EventCount[]; days: ActiveCount[]; weeks: ActiveCount[]; buys: readonly BuyCount[]; filter?: AppFilter; times?: readonly ScreenTime[]; transitions?: readonly Transition[]; exits?: readonly { screen: string; n: number }[] }): AppReport {
  const sum = (k: string) => r.counts.filter((c) => c.kind === k).reduce((a, c) => a + c.n, 0);
  const screens = new Map<string, { views: number; people: number; platforms: Set<string> }>();
  for (const c of r.counts.filter((x) => x.kind === "view")) {
    const s = screens.get(c.screen) ?? { views: 0, people: 0, platforms: new Set<string>() };
    s.views += c.n; s.people = Math.max(s.people, c.users); s.platforms.add(c.platform);
    screens.set(c.screen, s);
  }
  const taps = new Map<string, Map<string, { taps: number; people: number }>>();
  for (const c of r.counts.filter((x) => x.kind === "tap" && x.control)) {
    const m = taps.get(c.screen) ?? new Map();
    const t = m.get(c.control) ?? { taps: 0, people: 0 };
    t.taps += c.n; t.people = Math.max(t.people, c.users);
    m.set(c.control, t); taps.set(c.screen, m);
  }
  const flowN = (f: string, st: string) => r.counts.filter((c) => c.kind === "flow" && c.flow === f && c.stage === st).reduce((a, c) => a + c.n, 0);
  return {
    from: r.from, to: r.to, active: { days: r.days, weeks: r.weeks }, filter: r.filter ?? {},
    times: [...(r.times ?? [])].sort((a, b) => b.median - a.median || (a.screen < b.screen ? -1 : 1)).slice(0, 60),
    entries: (r.transitions ?? []).filter((t) => t.from === "").map((t) => ({ screen: t.to, n: t.n })).sort((a, b) => b.n - a.n).slice(0, 15),
    exits: [...(r.exits ?? [])].sort((a, b) => b.n - a.n).slice(0, 15),
    transitions: (r.transitions ?? []).filter((t) => t.from !== "").sort((a, b) => b.n - a.n).slice(0, 500),
    totals: { views: sum("view"), taps: sum("tap"), outbound: sum("out"), gotIt: sum("got_it") },
    screens: [...screens].map(([screen, s]) => ({ screen, views: s.views, people: s.people, platforms: [...s.platforms].sort() })).sort((a, b) => b.views - a.views || (a.screen < b.screen ? -1 : 1)).slice(0, 40),
    buttons: [...taps].map(([screen, m]) => ({
      screen, taps: [...m.values()].reduce((a, t) => a + t.taps, 0),
      controls: [...m].map(([control, t]) => ({ control, ...t })).sort((a, b) => b.taps - a.taps || (a.control < b.control ? -1 : 1)).slice(0, 10),
    })).sort((a, b) => b.taps - a.taps || (a.screen < b.screen ? -1 : 1)).slice(0, 25),
    flows: [...APP_FLOWS.map((flow) => ({ flow: flow as AppFlow | "buy", label: APP_FLOW_LABEL[flow], started: flowN(flow, "start"), finished: flowN(flow, "finish") })),
      // The buy flow: a buy link for a catalog item opened → "I got it".
      { flow: "buy" as const, label: "Buy: link opened → “I got it”", started: r.buys.filter((b) => b.item).reduce((a, b) => a + b.clickers, 0), finished: r.buys.filter((b) => b.item).reduce((a, b) => a + b.gotIt, 0) }]
      .map((x) => { const pct = pct1(x.finished, x.started); return { ...x, pct, dropOff: pct === null || pct > 100 ? null : Math.round((100 - pct) * 10) / 10 }; }),
    buys: r.buys.map((b) => ({
      item: b.item, placement: b.placement, placementLabel: BUY_PLACEMENT_LABEL[b.placement as BuyPlacement] ?? b.placement,
      clicks: b.clicks, people: b.clickers, gotIt: b.gotIt, notYet: b.notYet, rate: pct1(b.gotIt, b.clickers),
    })).sort((a, b) => b.clicks - a.clicks || (a.item < b.item ? -1 : 1)),
  };
}

/** The Monday a day's week starts on (YYYY-MM-DD). */
export function eventWeekStart(day: string): string {
  const t = Date.parse(`${day}T12:00:00Z`), dow = (new Date(t).getUTCDay() + 6) % 7;
  return new Date(t - dow * 86_400_000).toISOString().slice(0, 10);
}

export const APP_CSV_COLUMNS = ["day", "platform", "kind", "screen", "control", "item", "host", "placement", "flow", "stage", "events"] as const;
const csvCell = (v: string | number) => { const s = String(v); return /[",\n\r]/.test(s) || /^[=+\-@]/.test(s) ? `"${(/^[=+\-@]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"` : s; };
/** The daily counts as CSV (a spreadsheet-safe cell never starts with = + - @). */
export function appEventsCsv(rows: readonly DailyEventCount[]): string {
  const lines = [APP_CSV_COLUMNS.join(",")];
  for (const r of [...rows].sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : b.n - a.n))) {
    lines.push([r.day, r.platform, r.kind, r.screen, r.control, r.item, r.host, r.placement, r.flow, r.stage, r.n].map(csvCell).join(","));
  }
  return `${lines.join("\r\n")}\r\n`;
}
